import * as React from 'react'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { Text } from 'DVWebNodeUI'
import { PlayBox } from '../playButton/PlayButton'
import { StringId } from '../localizer/stringId'
import { TextLocalizer } from '../localizer/localizer'
import { isLiveEntityType } from '../../utils/entityTypes'
import { tryCatch } from '../../utils/tryCatch'
import { getPlaybackClickstreamRef } from '../../utils/getPlaybackClickstreamRef'
import { getPlaybackProgress } from '../../utils/getPlaybackProgress'
import {
  getFirstPlaybackData,
  getPlaybackSecondaryMessages,
} from '../../utils/playbackActionHelper'
import * as st from './episodePlayback.module.scss'
import * as cn from 'classnames'
import { DecodedText } from '../decodedText/decodedText'
import { withLocalization, LocalizerProps } from '../localizer/localizer'
import { PurchaseOverlay } from '../actionBox/disableOverlay'
import { widgetNameClasses } from '../../utils/widgetNameClasses'
import { epPurchasingText, isPurchasingEpisode, isPurchasingSeason } from '../../utils/actionBox'
import { ActionBoxTitleStatus } from '../../reducers/actionBox'
import { PurchaseOffer } from '../../selectors/purchase'
import { initializePlayer } from '../../integrations/player/index'
import { PlayerConfigData } from '../../integrations/player/config'
import { OnlineOfflinePlayback, OnlineOfflineRenderProps } from '../playback/playback'
import { OutputAction, UWPNotifier } from '@shared'

export interface IPlaybackProps {
  sessionID: string
  playerConfigData: PlayerConfigData
  title?: string
  isBonus?: boolean
  sequenceNumber?: number
  playbackActions?: WpsRuntime.atv.wps.PlaybackActionsModelProps
  notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]
  entityType?: string
  mainActionBox: ActionBoxTitleStatus | undefined
  epActionBox: ActionBoxTitleStatus | undefined
  useMFA: boolean
  usePlayer: boolean
  purchaseDetails: PurchaseOffer | null
}

type EpTitleProps = IPlaybackProps & LocalizerProps
type EpPlaybackProps = EpTitleProps & { playbackActions?: WpsRuntime.atv.wps.PlaybackActionsModelProps }

const getDeepLinkingData = (
  playbackAction: WpsRuntime.atv.wps.PlaybackActionDataProps,
  sessionID: string,
) => ({
  fallbackUrl: playbackAction.fallbackURL,
  titleId: playbackAction.playbackID,
  resumeTime: playbackAction.resumeTime,
  signInUrl: playbackAction.signInURL,
  sessionId: sessionID,
  ref: getPlaybackClickstreamRef(
    playbackAction.contentType,
    playbackAction.entitlementType
  ),
  videoType: playbackAction.videoMaterialType
})

const NotificationActionsMessage = ({ notificationActions }: { notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[] }) =>
  (<>
    {(notificationActions || [] as WpsRuntime.atv.wps.NotificationActionProps[]).filter(({severity}) =>
      !severity || ["CRITICAL", "ERROR"].includes(severity)
    ).map((notification) => (<span className={st.playbackNotifications}><Text string={notification.message} /></span>))}
  </>)

const PlaybackActionMessages = ({ messages }: { messages?: WpsRuntime.atv.wps.StringList }) =>
  messages && messages.length > 0
    ? <span className={st.playbackNotifications}>
      {messages.map((message, idx) => <PlaybackActionMessage key={idx} message={message} />)}
    </span>
    : null

const PlaybackActionMessage = ({ message }: { message: String }) =>
  <span className={st.playbackMessage}>
    <Text string={message} />
  </span>

const EpTitle = ({
  title,
  sequenceNumber,
  isBonus,
  entityType
}: {
  title?: string
  sequenceNumber?: number
  isBonus: boolean
  entityType?: string
}) => {
  if (!title) {
    return null
  }

  if (isLiveEntityType(entityType) || (!sequenceNumber && !isBonus)) {
    return <DecodedText string={title} />
  }

  if (isBonus) {
    return (
      <TextLocalizer
        stringID={StringId.DV_DP_EL_bonus_title_template}
        replace={{ episodeTitle: title }}
        shouldDecode={true}
      />
    )
  }

  return (
    <TextLocalizer
      stringID={StringId.DV_DP_EL_episode_title_template}
      replace={{
        sequenceNumber: sequenceNumber ? sequenceNumber.toString() : '',
        episodeTitle: title
      }}
      shouldDecode={true}
    />
  )
}

const epTitle = ({ title, useMFA, purchaseDetails, sequenceNumber, epActionBox, isBonus = false, entityType, playbackActions, notificationActions }: EpTitleProps, inProgress: boolean) => (
  <div className={`dv-episode-noplayback-title ${st.playboxUnavailable}`}>
    <div className={cn({
      [st.inProgress]: inProgress,
      [st.purchasing]: useMFA ? Boolean(purchaseDetails) : isPurchasingEpisode(epActionBox)
    }, st.title)}>
      <EpTitle
        title={title}
        sequenceNumber={sequenceNumber}
        isBonus={isBonus}
        entityType={entityType}
      />
    </div>
    <NotificationActionsMessage notificationActions={notificationActions} />
    <PlaybackActionMessages messages={getPlaybackSecondaryMessages(playbackActions)} />
  </div>
)

const epTitleWithPlayback = ({ title, playbackActions, playerConfigData, sessionID, sequenceNumber, isBonus = false, entityType, getString, usePlayer }: EpPlaybackProps) => {
  const playbackData = getFirstPlaybackData(playbackActions)

  if (!playbackData) {
    return null
  }

  const progress = getPlaybackProgress(playbackData.progress)
  const deeplinkingData = getDeepLinkingData(playbackData, sessionID)

  // build user-friendly accessibility description
  const ariaText = title ?
    (sequenceNumber ? getString(StringId.DV_DP_ARIA_watch_episode, { episodeNumber: sequenceNumber.toString(), episodeTitle: title })
      : getString(StringId.DV_DP_ARIA_watch_title, { title: title }))
    : undefined // fallback to use the not-that-user-friendly text description

  const playbackTitleId = playbackData.playbackID

  const triggerOnlinePlayback = (e: React.MouseEvent) => {
    const contentConfig = {
      asin: playbackTitleId,
      trailer: false,
      position: playbackData.resumeTime,
      ref: getPlaybackClickstreamRef(playbackData.contentType, playbackData.entitlementType),
      videoType: playbackData.videoMaterialType,
      useFullView: true,
    }

    initializePlayer(playerConfigData).play(contentConfig)
    e.preventDefault()
  }

  const triggerOfflinePlayback = (e: React.MouseEvent) => {
    UWPNotifier.getInstance().publish(OutputAction.PlayDownload, {
      titleId: playbackTitleId
    })
    e.preventDefault()
  }

  const renderPlayback = ({ downloadCompleted }: OnlineOfflineRenderProps) => {

    const handler = downloadCompleted ? triggerOfflinePlayback : triggerOnlinePlayback

    return (
      <PlayBox
        progress={progress}
        deepLinkingData={deeplinkingData}
        ariaLabel={ariaText}
        href={playbackData.playbackURL}
        onClick={usePlayer ? handler : undefined}
      >
        <div className={`js-episode-title-name ${st.playboxActionLabel}`}>
          <EpTitle
            title={title}
            sequenceNumber={sequenceNumber}
            isBonus={isBonus}
            entityType={entityType}
          />
        </div>
      </PlayBox>
    )
  }

  return (
    <div className={`dv-episode-playback-title ${st.playbox}`}>
      <OnlineOfflinePlayback playbackTitleId={playbackTitleId} render={renderPlayback} />
    </div>
  )
}

const EpisodePlaybackWithLocalization = tryCatch((props: EpTitleProps) => {
  const {
    playbackActions,
    useMFA,
    purchaseDetails,
    epActionBox,
    mainActionBox,
  } = props
  const playbackData = getFirstPlaybackData(playbackActions)
  const hasPlayback = Boolean(playbackData)

  const getPurchasingText = () => {
    if (hasPlayback) {
      return ''
    }
    return useMFA
      ? (purchaseDetails &&
          purchaseDetails.details &&
          purchaseDetails.details.offer.purchaseData.text) ||
          ''
      : epPurchasingText(epActionBox)
  }

  const inProgress = useMFA
    ? Boolean(purchaseDetails)
    : isPurchasingEpisode(epActionBox) || isPurchasingSeason(mainActionBox)

  return (
    <div className={`js-eplist-episode ${widgetNameClasses.playback}`} >
      {playbackData ? epTitleWithPlayback(props) : epTitle(props, inProgress)}
      {inProgress && <PurchaseOverlay className={st.progressWrapper} purchasingText={getPurchasingText()} useSmallSpinner />}
    </div>
  )
})

export const EpisodePlayback = withLocalization(EpisodePlaybackWithLocalization)